import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Box, Button, TextField, Stack, Chip, Alert, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, CircularProgress,
} from '@mui/material';
import { Add as AddIcon, ContentCopy as CopyIcon, Block as RevokeIcon, Link as LinkIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { approverLinksApi } from '@/services/api';
import { copiar } from '@/services/aprovador';

interface LinkRow {
  id: string;
  name: string;
  whatsappMasked: string;
  tokenHint: string;
  createdAt: string;
  revokedAt: string | null;
  lastVerifiedAt: string | null;
  lastSeenAt: string | null;
}

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

/** Links pessoais do painel /aprovar: gerar, copiar (só na criação) e revogar. */
const ApproverLinksCard: React.FC = () => {
  const [rows, setRows] = useState<LinkRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [novo, setNovo] = useState(false);
  const [nome, setNome] = useState('');
  const [whats, setWhats] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [gerado, setGerado] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await approverLinksApi.list();
      if (r.success) setRows(r.data as LinkRow[]);
    } catch { toast.error('Erro ao carregar links de aprovador'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const criar = async () => {
    setSalvando(true);
    try {
      const r = await approverLinksApi.create({ name: nome, whatsapp: whats });
      if (r.success) { setGerado((r.data as { url: string }).url); setNovo(false); setNome(''); setWhats(''); load(); }
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Erro ao gerar link');
    } finally { setSalvando(false); }
  };

  const revogar = async (row: LinkRow) => {
    if (!window.confirm(`Revogar o link de ${row.name}? Ele para de funcionar na hora.`)) return;
    try {
      await approverLinksApi.revoke(row.id);
      toast.success('Link revogado');
      load();
    } catch { toast.error('Erro ao revogar'); }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Painel do aprovador por link</Typography>
          <Typography variant="body2" color="text.secondary">
            Link pessoal para aprovar e dar baixa sem login. A cada 24 h pede um código no WhatsApp da pessoa.
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setNovo(true)}>Novo link</Button>
      </Box>
      <Divider sx={{ my: 1.5 }} />
      {loading ? <CircularProgress size={22} /> : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Nenhum link gerado.</Typography>
      ) : (
        <Stack divider={<Divider />}>
          {rows.map((r) => (
            <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <LinkIcon color={r.revokedAt ? 'disabled' : 'primary'} fontSize="small" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  {r.name} <Typography component="span" variant="caption" color="text.secondary">· {r.whatsappMasked} · …{r.tokenHint}</Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Criado {fmt(r.createdAt)} · último código {fmt(r.lastVerifiedAt)} · último acesso {fmt(r.lastSeenAt)}
                </Typography>
              </Box>
              {r.revokedAt ? <Chip size="small" label={`Revogado ${fmt(r.revokedAt)}`} /> : (
                <Tooltip title="Revogar"><IconButton size="small" color="error" onClick={() => revogar(r)}><RevokeIcon fontSize="small" /></IconButton></Tooltip>
              )}
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={novo} onClose={() => !salvando && setNovo(false)} fullWidth maxWidth="xs">
        <DialogTitle>Novo link de aprovador</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} fullWidth />
            <TextField label="WhatsApp (recebe o código)" placeholder="5537999999999" value={whats} onChange={(e) => setWhats(e.target.value)} fullWidth
              helperText="DDI + DDD + número, ou o id do WhatsApp (…@c.us)." />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNovo(false)} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={criar} disabled={salvando || !nome.trim() || !whats.trim()}>Gerar link</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!gerado} onClose={() => setGerado(null)} fullWidth maxWidth="sm">
        <DialogTitle>Link gerado</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Copie agora: o link não aparece de novo. Se perder, revogue e gere outro.</Alert>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', bgcolor: '#f6f8fb', p: 1.5, borderRadius: 1 }}>{gerado}</Typography>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CopyIcon />} onClick={async () => { if (gerado && await copiar(gerado)) toast.success('Link copiado'); }}>Copiar</Button>
          <Button variant="contained" onClick={() => setGerado(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ApproverLinksCard;
